import { useState } from 'react'
import { Widget } from './Widget'
import { type PulseCategory, pulseColor, pulseTextColor } from '../../utils/pulseCategory'
import { FaultBadge, faultText } from '../FaultBadge'

export type AttentionNodeType = 'pump' | 'reservoir' | 'edge'

// Generic enough for any focus's "demands attention" list, not just
// Infrastructure's — Maintenance's later real build reuses this rather
// than a second implementation (docs/plan-executive-dashboard-focuses.md).
export interface AttentionItem {
  id:       string
  label:    string
  detail:   string
  nodeType: AttentionNodeType
  // Reuses the map pulse's own classifier (ScadaView.tsx's
  // reservoirPulse/pumpPulse, via ../../utils/pulseCategory.ts) -- one
  // classifier for the whole app, not two independently-drifting ones.
  // 'positive' can't occur in practice: an item only exists in this list
  // because something needs attention.
  category: PulseCategory
  // Set alongside category for any 'problem' item — same Pulse.severity the
  // map pulse/zone fill use to tell a warning-orange item from a
  // critical-red one; absent (or on a non-'problem' item) reads as critical,
  // matching pulseColor()/pulseTextColor()'s own default.
  severity?: 'warning' | 'critical'
  // Pump kvar fault (backend `fault`/`fault_since`): a triangle badge on the
  // tile, additive to the color/category -- a running pump can still be
  // faulted, and that is exactly when someone needs to go check the station.
  fault?: boolean
  faultSince?: string | null
  // Only set for pump/reservoir — what DeviceDetailModal needs to look the
  // device up. Edge-node items have no matching modal (out of scope, see
  // DeviceDetailModal.tsx) and aren't clickable.
  deviceKey?: string
}

// Material Symbols Outlined path data -- literal domain-object pictographs
// (a real pump/tank silhouette) rather than generic stroke glyphs, per
// docs/plan-web-ux-cohesion.md's icon-set decision. `edge` reuses KpiCard's
// `dns` path (same "edge server" concept in the KPI row directly above this
// section) -- duplicated rather than imported so this stays a self-contained
// tile shape. Filled shapes, viewBox 0 -960 960 960 -- same rationale as
// AppShell.tsx's Icon and KpiCard.tsx's iconPaths.
//
// pump/reservoir are the user's own weight-100/grade--25 export from
// Google's icon tool (thinner than the wght400 default everything else in
// the app uses) -- a deliberate, disclosed one-off for these two tile
// icons specifically, not a general app-wide weight change.
const TYPE_ICON_PATH: Record<AttentionNodeType, string> = {
  pump:      'M140.5-274.5h237q-36.75-19.47-64.64-49.62T269.5-392.5h-129v118Zm485.25-60.26q60.75-60.26 60.75-145.25 0-84.99-60.47-145.74-60.47-60.75-145.75-60.75t-145.53 60.47q-60.25 60.47-60.25 145.75t60.26 145.53q60.26 60.25 145.25 60.25 84.99 0 145.74-60.26ZM691.5-568.5h129v-118h-237q36.08 20.55 63.79 50.53Q675-606 691.5-568.5Zm-573 344v-218h22v28H262q-5-15.5-7.25-31.75t-2.25-33.71q0-95.23 66.35-161.88Q385.21-708.5 480-708.5h340.5v-28h22v218h-21.97v-28H698q5 16.5 7.75 32.75t2.75 33.69q0 94.82-66.65 161.19Q575.21-252.5 480-252.5H140.5v28h-22Zm22-50v-118 118Zm680-294v-118 118ZM480-480Zm-36.25 59.49q-14.25-14.51-14.25-36.99 0-13.5 5.71-26.03 5.7-12.52 21.07-35.97l23.04-34.5 24.04 34.5Q521-493 526.25-481.25t5.25 23.75q0 22.48-14.68 36.99Q502.13-406 480.07-406q-22.07 0-36.32-14.51Z',
  reservoir: 'M280-314.5h400q69.31 0 117.91-48.17 48.59-48.18 48.59-117.25 0-69.08-48.59-117.83Q749.31-646.5 680-646.5H280q-69.11 0-117.3 48.53-48.2 48.53-48.2 117.75t48.2 117.47Q210.89-314.5 280-314.5ZM480-480ZM371.5-668.5h218v-21q0-13.5-9.25-22.75t-22.75-9.25h-154q-14 0-23 9.25t-9 22.75v21ZM280-224v-68.5q-78.5 0-133-54.35-54.5-54.36-54.5-133 0-78.65 54.4-133.65 54.39-55 133.1-55h66.5v-24q0-21.71 15.61-37.86 15.62-16.14 38.39-16.14h160q21.71 0 37.86 16.14 16.14 16.15 16.14 37.86v24H680q78.92 0 133.71 54.71 54.79 54.7 54.79 133.5 0 78.79-54.79 133.29-54.79 54.5-133.71 54.5v68.5h-22v-68.5H302v68.5h-22Z',
  edge:      'M300-720q-25 0-42.5 17.5T240-660q0 25 17.5 42.5T300-600q25 0 42.5-17.5T360-660q0-25-17.5-42.5T300-720Zm0 400q-25 0-42.5 17.5T240-260q0 25 17.5 42.5T300-200q25 0 42.5-17.5T360-260q0-25-17.5-42.5T300-320ZM160-840h640q17 0 28.5 11.5T840-800v280q0 17-11.5 28.5T800-480H160q-17 0-28.5-11.5T120-520v-280q0-17 11.5-28.5T160-840Zm40 80v200h560v-200H200Zm-40 320h640q17 0 28.5 11.5T840-400v280q0 17-11.5 28.5T800-80H160q-17 0-28.5-11.5T120-120v-280q0-17 11.5-28.5T160-440Zm40 80v200h560v-200H200Zm0-400v200-200Zm0 400v200-200Z',
}

// Regular weight (400), NOT the weight-100 style used for TYPE_ICON_PATH's
// pump/reservoir -- this glyph renders much smaller (confined to the
// safe-zone cutout, ~30x19px for pump) than the 112px tile icons, and at
// that size weight-100's thin strokes were nearly invisible (the user's own
// live-browser catch). The tile icons stay thin; only this one glyph is
// full weight, since it's the one that actually needs to read at tiny size.
const NO_SIGNAL_ICON_PATH = 'M791-55 325-521q-2 10-3.5 20t-1.5 21q0 33 12.5 62t34.5 51l-57 57q-32-33-51-76.5T240-480q0-28 6-54t17-49l-59-59q-21 36-32.5 76.5T160-480q0 67 25 125t69 101l-57 57q-54-55-85.5-127.5T80-480q0-62 17.5-117T146-700l-91-91 57-57 736 736-57 57Zm23-205-58-58q21-36 32.5-76.5T800-480q0-67-25-125t-69-101l57-57q54 54 85.5 126.5T880-480q0 61-17.5 116.5T814-260ZM697-377l-62-62q2-10 3.5-20t1.5-21q0-33-12.5-62T593-593l57-57q32 32 51 75.5t19 94.5q0 28-6 54t-17 49Z'

// Icon-as-box tile treatment (docs/plan-icon-as-box.md): the icon
// silhouette itself is the tile, replacing the old circle/tank/square
// background shape -- the pump/tank/server pictograph already conveys
// type unambiguously once it's the shape, so a separate rounding cue for
// the same fact was redundant.
//
// Third iteration on how the fault-text sits inside the icon. Attempt 1
// centered text over the icon's full bounding box (spilled onto the
// pump's side pipes / the tank's legs). Attempt 2 fixed the position but
// colored the text the same as the icon fill, which overwatch's own
// point-in-polygon check proved doesn't reliably land on a hole (the
// pump's dead center is solid fill). This version doesn't depend on the
// icon's own hole geometry at all: an SVG <mask> punches an actual
// rectangular window through the icon fill (white keeps the icon
// visible, black cuts a hole), so the text always lands on a guaranteed,
// literal gap -- see SAFE_ZONE below for the exact per-icon geometry
// (computed from the real path data, not eyeballed) and the mask
// mechanics inside AttentionTile.
//
// Only pump/reservoir are precisely measured -- confirmed as the only
// two icons currently in scope. `edge`'s box is a generic centered
// placeholder, not verified against the dns path's own geometry, since
// edge tiles weren't asked for yet.
const SAFE_ZONE: Record<AttentionNodeType, { x: number; y: number; w: number; h: number }> = {
  pump:      { x: 350, y: -560, w: 260, h: 160 },
  reservoir: { x: 300, y: -540, w: 360, h: 120 },
  edge:      { x: 340, y: -580, w: 280, h: 200 }, // generic placeholder, not measured
}

// Converts a SAFE_ZONE rect (in the icon's own 0 -960 960 960 viewBox units)
// to CSS percentages for the absolutely-positioned text <span> -- percentages
// so the text tracks the mask's cutout regardless of the tile's rendered
// pixel size (56px -> 112px here, or any future resize) without needing to
// recompute anything. The Y flip is the one gotcha: this viewBox's origin is
// top-left at (0,-960), so a y of -960 is the visual top (CSS top: 0%) and a
// y of 0 is the visual bottom (CSS top: 100%).
function safeZoneStyle(zone: { x: number; y: number; w: number; h: number }) {
  return {
    top:    `${(zone.y + 960) / 960 * 100}%`,
    left:   `${zone.x / 960 * 100}%`,
    width:  `${zone.w / 960 * 100}%`,
    height: `${zone.h / 960 * 100}%`,
  }
}

function AttentionTile({ item, onOpen }: { item: AttentionItem; onOpen?: () => void }) {
  const clickable = onOpen !== undefined
  const Tag = clickable ? 'button' : 'div'
  const fillColor = pulseColor(item)
  const textColor = pulseTextColor(item)
  const zone = SAFE_ZONE[item.nodeType]
  // Unique per rendered tile, not per node type -- reusing one static id
  // across multiple <mask> elements in the same list would make every
  // instance but the first reference the wrong (last-defined) mask in
  // some browsers, a real cross-rendering gotcha with inline SVG masks
  // inside a .map().
  const maskId = `attention-tile-mask-${item.id}`

  // Disconnected tiles show a no-signal glyph instead of text -- the labels
  // for that category ("Nema podataka", "Bez odgovora") are long enough to
  // wrap to 2 lines and not fit the cutout comfortably, where "problem"
  // tiles' detail is normally a short (often 3-digit) reading that does.
  // The detail string isn't lost, just moved to a title tooltip.
  const showIcon = item.category === 'disconnected'

  return (
    <Tag
      onClick={onOpen}
      title={item.fault ? `${item.detail} · ${faultText(item.faultSince)}` : item.detail}
      className={`flex flex-col items-center gap-1.5 ${clickable ? 'cursor-pointer group' : ''}`}
    >
      <span className="text-xs font-semibold text-gray-800 text-center truncate max-w-[104px]">{item.label}</span>
      <div className={`relative w-28 h-28 shrink-0 transition-opacity ${clickable ? 'group-hover:opacity-80' : ''}`}>
        <svg className="absolute inset-0 w-full h-full" viewBox="0 -960 960 960">
          <defs>
            <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="-960" width="960" height="960">
              {/* White = icon stays visible. Black = cut a hole through it. */}
              <rect x="0" y="-960" width="960" height="960" fill="white" />
              <rect x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx="20" fill="black" />
            </mask>
          </defs>
          <path d={TYPE_ICON_PATH[item.nodeType]} fill={fillColor} mask={`url(#${maskId})`} />
        </svg>
        {showIcon ? (
          // No explicit sizing beyond the safe-zone box -- SVG's default
          // preserveAspectRatio (xMidYMid meet) fits this square glyph
          // inside the (often non-square) zone without stretching it.
          <svg className="absolute" style={safeZoneStyle(zone)} viewBox="0 -960 960 960" fill={textColor}>
            <path d={NO_SIGNAL_ICON_PATH} />
          </svg>
        ) : (
          <span
            className="absolute flex items-center justify-center text-center leading-none font-bold text-xs"
            style={{ ...safeZoneStyle(zone), color: textColor }}
          >
            {item.detail}
          </span>
        )}
        {item.fault && (
          <span className="absolute -top-1 -right-1">
            <FaultBadge since={item.faultSince} critical={item.severity === 'critical'} size={28} />
          </span>
        )}
      </div>
    </Tag>
  )
}

// Mirrors mobile's Infrastruktura tab spec exactly (docs/plan-mobile-
// scada-field-views.md §4): collapsed shows a count, expanded shows the
// list, orange border + default-open only when the count is nonzero.
// `manualOpen` starts null so openness tracks `hasItems` live until the
// user actually clicks the header — otherwise a count that arrives after
// first paint (async fetch) would never auto-open.
export function DemandsAttentionSection({ title, items, onDeviceClick }: {
  title: string
  items: AttentionItem[]
  // Only pump/reservoir items call this (edge items have no detail modal).
  onDeviceClick?: (deviceKey: string, nodeType: 'pump' | 'reservoir') => void
}) {
  const hasItems = items.length > 0
  const [manualOpen, setManualOpen] = useState<boolean | null>(null)
  const open = manualOpen ?? hasItems

  return (
    <Widget accent={hasItems ? 'border-brand-warning' : 'border-gray-100'}>
      <button
        onClick={() => setManualOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-base font-semibold text-gray-900 truncate">{title}</h2>
          {hasItems && (
            <span className="text-xs bg-orange-50 text-brand-warning-text font-semibold px-2 py-0.5 rounded-full border border-orange-200 shrink-0">
              {items.length}
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {items.length === 0 ? (
            <p className="text-sm text-gray-400">Nema stavki koje zahtevaju pažnju.</p>
          ) : (
            <div className="flex flex-wrap gap-x-4 gap-y-3">
              {items.map((it) => (
                <AttentionTile
                  key={it.id}
                  item={it}
                  onOpen={
                    it.deviceKey && (it.nodeType === 'pump' || it.nodeType === 'reservoir') && onDeviceClick
                      ? () => onDeviceClick(it.deviceKey!, it.nodeType as 'pump' | 'reservoir')
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Widget>
  )
}
